import { useLocation, useNavigate, useParams } from 'react-router-dom'
import MessageAdminData from '@/components/sponsorship/MessageAdminData'
import ChatContactsList from '@/components/sponsorship/ChatContactsList'
import { tokenService } from '@/lib/token.service'
import { useMemo, useState, useEffect } from 'react'
import type { User } from '@/schemas/chat.schema'
import { isUserSuperAdmin } from '@/utils/helper'
import { Loader2, ShieldAlert } from 'lucide-react'
import { ArrowRightIcon } from '@/components/Icons'
import { useUserRole } from '@/contexts/UserRoleContext'

function MessageAdmin() {
  const location = useLocation()
  const breadcrumbText = location.state?.breadcrumbText || 'Dashboard'
  const { id } = useParams()
  const user = tokenService.getUser()
  const navigate = useNavigate()
  const isSuperAdmin = isUserSuperAdmin()
  const isSuperAdminOwnChat = isSuperAdmin && (!id || id === user.id)
  const [selectedContactId, setSelectedContactId] = useState<
    string | undefined
  >(isSuperAdmin && !isSuperAdminOwnChat ? id : undefined)

  const [selectedRoomId, setSelectedRoomId] = useState<string | undefined>(
    undefined
  )
  const [loadingRooms, setLoadingRooms] = useState(
    !isSuperAdmin && !isSuperAdminOwnChat
  )
  const [roomsError, setRoomsError] = useState<string | null>(null)
  const { currentUser: localUser } = useUserRole()

  const isSuperAdminWithoutChatAuth =
    isSuperAdmin && localUser && !localUser.isChatAdmin

  const currentUser: User = useMemo(() => {
    return {
      id: user.id,
      name: `${user.firstName} ${user.lastName}`,
    }
  }, [user])

  const otherUserId = useMemo(() => {
    return isSuperAdmin && !isSuperAdminOwnChat ? id : selectedContactId
  }, [isSuperAdmin, isSuperAdminOwnChat, id, selectedContactId])

  const roomId = useMemo(() => {
    if (isSuperAdmin && !isSuperAdminOwnChat) {
      return id ? `${user.id}${id}` : ''
    } else {
      return selectedRoomId || ''
    }
  }, [user.id, id, selectedRoomId, isSuperAdmin, isSuperAdminOwnChat])

  useEffect(() => {
    if (isSuperAdmin || isSuperAdminOwnChat) return

    const fetchSponsorChatRoom = () => {
      setSelectedContactId(localUser?.chatAdminId)
      setSelectedRoomId(`${localUser?.chatAdminId}${user.id}`)
      setLoadingRooms(false)
      setRoomsError(null)
    }

    fetchSponsorChatRoom()
  }, [user.id, isSuperAdmin, isSuperAdminOwnChat, localUser])

  const handleContactSelect = (contactId: string, roomId?: string) => {
    setSelectedContactId(contactId)
    setSelectedRoomId(roomId)
  }

  const renderMainContent = () => {
    if (isSuperAdminWithoutChatAuth) {
      return (
        <div className="flex-1 h-full flex items-center justify-center mt-32">
          <div className="text-center max-w-md px-4">
            <div className="w-16 h-16 bg-ss-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <ShieldAlert
                className="w-8 h-8 text-ss-red-500"
                aria-hidden="true"
              />
            </div>
            <h2 className="text-xl font-semibold text-ss-neutral-950 mb-2">
              You don't have authorization for chats
            </h2>
            <p className="text-sm text-ss-neutral-500 mb-6">
              Only assigned chat admin can access messaging features.
            </p>
          </div>
        </div>
      )
    }

    if (loadingRooms) {
      return (
        <div className="flex-1 h-full flex items-center justify-center">
          <div className="text-center">
            <Loader2 className="h-8 w-8 animate-spin text-ss-primary-500 mx-auto mb-4" />
            <p className="text-sm text-ss-neutral-500">
              Loading conversations...
            </p>
          </div>
        </div>
      )
    }

    if (roomsError) {
      return (
        <div className="flex-1 h-full flex items-center justify-center">
          <div className="text-center">
            <h2 className="text-xl font-semibold text-ss-neutral-950 mb-2">
              {roomsError}
            </h2>
            <p className="text-sm text-ss-neutral-500">
              Please try again later
            </p>
          </div>
        </div>
      )
    }

    if (otherUserId && roomId) {
      return (
        <MessageAdminData
          currentUser={currentUser}
          otherUserId={otherUserId}
          roomId={roomId}
          otherUserName={isSuperAdmin ? undefined : 'SlopeSync Admin'}
          isSSAdmin={isSuperAdmin}
        />
      )
    }

    return (
      <div className="flex-1 h-full flex items-center self-center justify-center">
        <div className="text-center max-w-md px-4">
          <div className="w-16 h-16 bg-ss-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg
              className="w-8 h-8 text-ss-blue-500"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
              />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-ss-neutral-950 mb-2">
            {isSuperAdminOwnChat
              ? 'Select a Conversation'
              : 'No Conversation Available'}
          </h2>
          <p className="text-sm text-ss-neutral-500">
            {isSuperAdminOwnChat
              ? 'Choose a contact from the list to start messaging'
              : "You don't have any active conversations yet"}
          </p>
        </div>
      </div>
    )
  }

  const shouldRenderContactList =
    isSuperAdmin && isSuperAdminOwnChat && !isSuperAdminWithoutChatAuth

  return (
    <main
      className="@container/main flex flex-1 flex-col h-full"
      id="main-content"
      tabIndex={-1}
      aria-label="Message Admin Main Content"
    >
      {/* Header with breadcrumbs */}
      <section aria-label="Message Admin Header" className="p-4 pb-6">
        {/* Breadcrumbs */}
        {!isSuperAdminOwnChat && (
          <nav aria-label="Breadcrumb" className="mb-2">
            <ol className="flex items-center space-x-2 text-sm">
              <li>
                <button
                  onClick={() => navigate(-1)}
                  className="text-ss-neutral-600 hover:text-ss-blue-500 font-semibold"
                  aria-label={`Go to ${breadcrumbText}`}
                >
                  {breadcrumbText}
                </button>
              </li>
              <li>
                <ArrowRightIcon
                  className="h-3.5 w-3.5 text-ss-neutral-400"
                  aria-hidden="true"
                />
              </li>
              <li>
                <span
                  className="text-ss-blue-500 font-semibold"
                  aria-current="page"
                >
                  Message {isSuperAdmin ? 'Sponsor' : 'Admin'}
                </span>
              </li>
            </ol>
          </nav>
        )}

        {/* Page title and description */}
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex-1 min-w-0">
            <h1 className="text-2xl font-bold text-ss-neutral-950 leading-8">
              Message {isSuperAdmin ? 'Sponsor' : 'Admin'}
            </h1>
            <p className="text-sm text-ss-neutral-600 leading-relaxed mt-1">
              {isSuperAdmin
                ? "Start a conversation with the sponsor regarding their ads. All messages will be logged under the sponsor's profile for future reference."
                : 'Conversation with the Admin regarding your ads.'}
            </p>
          </div>
        </div>
      </section>

      <div className="flex flex-1 h-full gap-0">
        {/* Contact list for Super Admin's own chat */}
        {shouldRenderContactList && (
          <ChatContactsList
            currentUserId={user.id}
            selectedContactId={selectedContactId}
            onContactSelect={handleContactSelect}
          />
        )}

        {/* Main content area */}
        {renderMainContent()}
      </div>
    </main>
  )
}

export default MessageAdmin
